import { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';

const KONAMI_CODE = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'b',
  'a',
];

export const useKonamiCode = () => {
  const [keys, setKeys] = useState<string[]>([]);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  // Inject CSS animations on mount
  useEffect(() => {
    const styleId = 'konami-code-styles';
    
    // Check if styles already exist
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        
        @keyframes fall {
          to {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
        
        .coffee-mode {
          filter: sepia(0.3) saturate(1.2);
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Log fun message to console on mount
  useEffect(() => {
    console.log(
      "%c  ____  _             _  _   _       _       \n / ___|| |_ _   _  __| || \\ | | ___ | |_ ___ \n \\___ \\| __| | | |/ _` ||  \\| |/ _ \\| __/ _ \\\n  ___) | |_| |_| | (_| || |\\  | (_) | ||  __/\n |____/ \\__|\\__,_|\\__,_||_| \\_|\\___/ \\__\\___|",
      "color: #3b82f6; font-weight: bold; font-size: 12px; font-family: monospace;"
    );
    console.log(
      "%c🎓 Found the secret dev console! You're a curious one.",
      "color: #3b82f6; font-size: 14px; font-weight: bold;"
    );
    console.log(
      "%c☕ Want to work with us? hello@studnote.com",
      "color: #10b981; font-size: 13px;"
    );
    console.log(
      "%c🐛 Found a bug? We'd love to hear about it!",
      "color: #f59e0b; font-size: 13px;"
    );
    console.log(
      "%c💡 Pro tip: Try the Konami code on any page (↑ ↑ ↓ ↓ ← → ← → B A) ;)",
      "color: #8b5cf6; font-size: 13px; font-style: italic;"
    );
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      setKeys((prevKeys) => {
        const newKeys = [...prevKeys, e.key].slice(-10); // Keep only last 10 keys
        
        // Check if konami code is matched
        if (newKeys.join(',') === KONAMI_CODE.join(',')) {
          activateCaffeineMode();
          return []; // Reset
        }
        
        return newKeys;
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      // Clear all pending timeouts on unmount
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  const activateCaffeineMode = () => {
    // Clear any previous timeouts
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    // Add screen shake
    document.body.style.animation = 'shake 0.5s';
    
    toast('☕ CAFFEINE MODE ACTIVATED! Maximum productivity unlocked!', {
      duration: 5000,
      className: 'font-bold text-lg',
    });

    // Create coffee rain effect
    createCoffeeRain();

    // Apply coffee theme temporarily
    document.documentElement.style.setProperty('--coffee-mode', '1');
    document.body.classList.add('coffee-mode');

    // Reset after 30 seconds
    const resetTimeout = setTimeout(() => {
      document.body.style.animation = '';
      document.body.classList.remove('coffee-mode');
      document.documentElement.style.removeProperty('--coffee-mode');
    }, 30000);
    
    timeoutsRef.current.push(resetTimeout);
  };

  const createCoffeeRain = () => {
    const container = document.createElement('div');
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      pointer-events: none;
      z-index: 9999;
      overflow: hidden;
    `;
    document.body.appendChild(container);

    // Create 30 coffee cups
    for (let i = 0; i < 30; i++) {
      const coffeeTimeout = setTimeout(() => {
        if (!document.body.contains(container)) return; // Safety check
        
        const coffee = document.createElement('div');
        coffee.textContent = '☕';
        coffee.style.cssText = `
          position: absolute;
          top: -50px;
          left: ${Math.random() * 100}%;
          font-size: ${Math.random() * 20 + 20}px;
          animation: fall ${Math.random() * 3 + 2}s linear forwards;
        `;
        container.appendChild(coffee);

        const removeTimeout = setTimeout(() => {
          if (coffee.parentNode) {
            coffee.remove();
          }
        }, 5000);
        
        timeoutsRef.current.push(removeTimeout);
      }, i * 100);
      
      timeoutsRef.current.push(coffeeTimeout);
    }

    // Remove container after animation
    const containerTimeout = setTimeout(() => {
      if (container.parentNode) {
        container.remove();
      }
    }, 5000);
    
    timeoutsRef.current.push(containerTimeout);
  };
};
